import { Controller, useForm } from 'react-hook-form';
import { Button } from 'src/components/ui/button';
import React, { useEffect, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  booleanScheme,
  emailScheme,
  stringScheme,
  urlZendeskArticlesScheme,
} from 'src/utils/validation-schemes';
import Input from 'src/components/Input/Input';
import { ZENDESK_HELP_CENTER_URL } from 'src/constants/urls';
import {
  useCreateKnowledgeBaseCollectorMutation,
  useGetKnowledgeBaseCollectorsQuery,
  useLazyGetKnowledgeBaseCollectorsQuery,
  useLazyGetKnowledgeBaseCollectorZendeskConsentLinkQuery,
  useRunKnowledgeBaseCollectorMutation,
  useSetupKnowledgeBaseCollectorMutation,
  useUpdateKnowledgeBaseCollectorMutation,
} from 'src/services/knowledgeBaseService';
import { useParams } from 'react-router-dom';
import { useNotification } from 'src/hooks/useNotification';
import { useLoader } from 'src/provider/LoaderProvider';
import { generateUniqueName, INTEGRATION_NAMES_MAP } from 'src/utils/fileUtils';
import { ZendeskHelpCenterIsPrivate } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/CreateZendeskIntegrationPopup/ZendeskIntegrationForm2/ZendeskHelpCenterFields/ZendeskHelpCenterIsPrivate';
import { ZendeskHelpCenterExcludedUrls } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/CreateZendeskIntegrationPopup/ZendeskIntegrationForm2/ZendeskHelpCenterFields/ZendeskHelpCenterExcludedUrls';
import * as yup from 'yup';
import { Switch } from 'src/components/ui/switch';
import { InputInfoToolTip } from 'src/components/Input/InputInfoToolTip';
import { getSubdomainHelper } from 'src/helpers/getSubdomainHelper';
import { useAppSelector } from 'src/store';
import { ZendeskConnectSection } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/CreateZendeskIntegrationPopup/ZendeskIntegrationForm2/ZendeskHelpCenterFields/ZendeskConnectSection';
import { ZendeskHelpCenterAddUrls } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/CreateZendeskIntegrationPopup/ZendeskIntegrationForm2/ZendeskHelpCenterFields/ZendeskHelpCenterAddUrls';
import { Spinner } from 'src/components/Spinner/Spinner';
import ArrowLargeGreyIcon from 'src/assets/img/arrow-large-grey-icon.svg';
import { AUTH_STATUS_POLL_INTERVAL_MS, AUTH_STATUS_POLL_TIMEOUT_MS } from 'src/shared/constants';

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const ZendeskHelpCenterForm = ({
  zendeskIntegrationType,
  prevStep = () => {},
  closePopup = (..._args: any[]) => {},
  backButtonVisible = true,
  knowledgeBaseId,
  initialRequest = {} as any,
}: {
  zendeskIntegrationType?: any;
  prevStep?: () => void;
  closePopup?: (...args: any[]) => void;
  backButtonVisible?: boolean;
  knowledgeBaseId?: any;
  initialRequest?: any;
}) => {
  const { id } = useParams();
  const notification = useNotification();
  const loader = useLoader();
  const { isTrialPlan } = useAppSelector((s) => s.planSlice);
  const [
    createCollector,
    {
      data: createCollectorData,
      isLoading: isLoadingCreateCollector,
      isSuccess: isSuccessCreateCollector,
    },
  ] = useCreateKnowledgeBaseCollectorMutation() as any;

  const [
    setupCollector,
    { isLoading: isLoadingSetupCollector, isSuccess: isSuccessSetupCollector },
  ] = useSetupKnowledgeBaseCollectorMutation();

  // Separate instance so creating the collector from "Connect with Zendesk" doesn't trigger
  // the Integrate success flow (notification, setup and closing the popup).
  const [createConnectCollector] = useCreateKnowledgeBaseCollectorMutation();
  const [updateCollector] = useUpdateKnowledgeBaseCollectorMutation();
  const [runCollector] = useRunKnowledgeBaseCollectorMutation();
  const [getZendeskConsentLink] = useLazyGetKnowledgeBaseCollectorZendeskConsentLinkQuery();
  const [getCollectors] = useLazyGetKnowledgeBaseCollectorsQuery();
  const kbId = knowledgeBaseId || id;
  const [isManualConnectOpen, setIsManualConnectOpen] = useState(false);
  // The collector created by "Connect with Zendesk"; Integrate updates it instead of creating another.
  const [connectedCollector, setConnectedCollector] = useState(null);
  const [isCheckingAuthStatus, setIsCheckingAuthStatus] = useState(false);
  const isMountedRef = useRef(true);
  const isPollingAuthStatusRef = useRef(false);
  // Once Zendesk OAuth is authorized, the manual API token route is locked.
  const isZendeskAuthorized = connectedCollector?.auth_status === 'ok';
  const isManualConnectActive = isManualConnectOpen && !isZendeskAuthorized;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const { data: collectorsResponse, isLoading: isLoadingCollectors } =
    useGetKnowledgeBaseCollectorsQuery(
      {
        id: knowledgeBaseId || id,
        include_data: false,
      },
      {
        skip: !(knowledgeBaseId || id),
      },
    );

  let type = null;

  switch (zendeskIntegrationType) {
    case 'Help Center':
      type = 'zendesk_helpcenter';
      break;
    case 'Tickets':
      type = 'zendesk_tickets';
      break;
  }

  useEffect(() => {
    if (isSuccessCreateCollector) {
      notification.success(`${createCollectorData.name} Added Successfully.`);

      if (createCollectorData.enabled) {
        const body = {
          type: createCollectorData.type,
          name: `[CoSupportAI] Article Auto-Sync (${createCollectorData.name})`,
        };

        setupCollector({
          knowledgeBaseId: knowledgeBaseId || id,
          collectorId: createCollectorData.id,
          body,
        });
      } else {
        close(true);
      }
    }
  }, [isSuccessCreateCollector]);

  useEffect(() => {
    if (isSuccessSetupCollector && createCollectorData) {
      notification.success(`${createCollectorData.name} Setup Successfully.`);
      close(true);
    }
  }, [isSuccessSetupCollector]);

  useEffect(() => {
    if (isLoadingCreateCollector || isLoadingSetupCollector || isLoadingCollectors) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoadingCreateCollector, isLoadingSetupCollector, isLoadingCollectors]);

  const prev = () => {
    prevStep();
  };

  const close = (shouldCloseSourceModal = false) => {
    closePopup(shouldCloseSourceModal);
  };

  const {
    handleSubmit,
    control,
    watch,
    formState: { isDirty, isValid, errors },
    clearErrors,
  } = useForm<any>({
    defaultValues: {
      domain: '',
      zendesk_email: '',
      zendesk_api_key: '',
      zendesk_subdomain: '',
      brand_id: '',
      type: type,
      is_private: 'public',
      enabled: false,
      name: '',
      excluded_articles: [],
      source_links: [],
    },
    resolver: yupResolver(
      yup.lazy((values, { context }) => {
        const isRequired = values.is_private === 'private' || values.enabled;
        // Email and API key are only needed when connecting manually instead of via Zendesk OAuth.
        const isManualRequired = isRequired && Boolean(context?.isManualConnectActive);

        const baseSchema = {
          enabled: booleanScheme({ required: true }),
          is_private: stringScheme({ required: true }),
          domain: stringScheme({ required: true }).label('Zendesk Help Center Domain'),
          zendesk_subdomain: stringScheme({ required: isRequired }).label('Zendesk API Subdomain'),
          zendesk_email: emailScheme({
            required: isManualRequired,
            allowEmpty: !isManualRequired,
          }).label('Zendesk Email'),
          zendesk_api_key: stringScheme({ required: isManualRequired }).label('Zendesk API Key'),
          brand_id: stringScheme({ required: isRequired }).label('Zendesk Brand ID'),
          excluded_articles: yup.array(urlZendeskArticlesScheme({ required: false })),
          source_links: yup.array(urlZendeskArticlesScheme({ required: false })),
        };

        return yup.object({
          ...baseSchema,
        });
      }),
    ),
    context: { isManualConnectActive },
  });

  const runImmediately =
    typeof initialRequest.run_immediately === 'undefined' ? true : initialRequest.run_immediately;

  const buildBody = (formData) => {
    const data = { ...formData };
    const isPrivate = data.is_private === 'private';
    const name = `${INTEGRATION_NAMES_MAP[data.type]} (${data.zendesk_subdomain || data.domain})`;

    data.collect_private_articles = isPrivate;
    delete data.is_private;

    if (!isPrivate && !data.enabled) {
      delete data.brand_id;
      delete data.zendesk_api_key;
      delete data.zendesk_email;
      delete data.zendesk_subdomain;
    }

    // Credentials come from Zendesk OAuth unless the manual API token panel is used.
    if (!isManualConnectActive) {
      delete data.zendesk_api_key;
      delete data.zendesk_email;
    }

    return {
      ...data,
      run_immediately: runImmediately,
      ml_uploader_type: 'customer',
      name: generateUniqueName(name, collectorsResponse),
    };
  };

  // Polls the knowledge base collectors until the Zendesk OAuth consent is granted (auth_status "ok").
  // Resolves with the authorized collector, or null on timeout or when the form unmounts.
  const waitForZendeskAuthorization = async (collectorId) => {
    const deadline = Date.now() + AUTH_STATUS_POLL_TIMEOUT_MS;

    while (Date.now() < deadline) {
      await wait(AUTH_STATUS_POLL_INTERVAL_MS);
      if (!isMountedRef.current) return null;

      const { data: collectors } = await getCollectors({ id: kbId, include_data: true });
      if (!isMountedRef.current) return null;

      const collector = collectors?.find(({ id }) => id === collectorId);
      if (collector?.auth_status === 'ok') {
        return collector;
      }
    }

    return null;
  };

  const pollZendeskAuthorization = async (collector) => {
    if (isPollingAuthStatusRef.current) return;
    isPollingAuthStatusRef.current = true;
    setIsCheckingAuthStatus(true);

    const authorizedCollector = await waitForZendeskAuthorization(collector.id);
    isPollingAuthStatusRef.current = false;

    if (!isMountedRef.current) return;
    setIsCheckingAuthStatus(false);

    if (authorizedCollector) {
      setConnectedCollector({ ...collector, ...authorizedCollector });
      notification.success('Zendesk account was connected.');
    } else {
      notification.error('Zendesk authorization timed out. Please try again.');
    }
  };

  const onConnectWithZendesk = async (formData) => {
    let collector = connectedCollector;

    if (!collector) {
      loader.show();
      // Runs after authorization, when Integrate is pressed.
      const { data: createdCollector, error } = await createConnectCollector({
        id: kbId,
        body: { ...buildBody(formData), run_immediately: false },
      });
      loader.hide();

      if (error) return;
      collector = createdCollector;
      setConnectedCollector(createdCollector);
    } else {
      // Zendesk authorizes the collector as stored, so the current values (e.g. an edited
      // subdomain) are saved before requesting consent again.
      loader.show();
      const { error } = await updateCollector({
        knowledgeBaseId: kbId,
        collectorId: collector.id,
        body: { ...buildBody(formData), name: collector.name, run_immediately: false },
      });
      loader.hide();

      if (error) return;
    }

    const { data: consentLink } = await getZendeskConsentLink({
      knowledgeBaseId: kbId,
      collectorId: collector.id,
    });

    if (consentLink?.authorize_url) {
      window.open(consentLink.authorize_url);
      pollZendeskAuthorization(collector);
    }
  };

  // Runs the same validation as submit; the connect handler only fires for a valid form.
  const connectWithZendesk = handleSubmit(onConnectWithZendesk);

  const integrateConnectedCollector = async (formData) => {
    const body = { ...buildBody(formData), name: connectedCollector.name };

    loader.show();
    const { error } = await updateCollector({
      knowledgeBaseId: kbId,
      collectorId: connectedCollector.id,
      body,
    });

    if (!error && runImmediately) {
      await runCollector({ knowledgeBaseId: kbId, collectorId: connectedCollector.id });
    }

    if (!error && body.enabled) {
      await setupCollector({
        knowledgeBaseId: kbId,
        collectorId: connectedCollector.id,
        body: {
          type: body.type,
          name: `[CoSupportAI] Article Auto-Sync (${connectedCollector.name})`,
        },
      });
    }
    loader.hide();

    if (!error) {
      notification.success(`${connectedCollector.name} Added Successfully.`);
      close(true);
    }
  };

  const submit = (data) => {
    if (!isValid) return;

    if (connectedCollector) {
      return integrateConnectedCollector(data);
    }

    createCollector({ id: kbId, body: buildBody(data) });
  };

  const isPrivate = watch('is_private') === 'private';
  const enabled = watch('enabled');
  const domain = watch('domain');
  const needsCredentials = isPrivate || enabled;
  const isIntegrateDisabled =
    !domain || (needsCredentials && !isManualConnectActive && !isZendeskAuthorized);

  return (
    <form
      onSubmit={handleSubmit(submit)}
      id="zendesk-integration-form-2"
      className={'h-full flex flex-col justify-between'}
    >
      {isCheckingAuthStatus && (
        <div
          className={
            'absolute flex items-center justify-center w-full h-full top-0 left-0 bg-ebony_opacity_30 backdrop-blur-sm z-50'
          }
        >
          <div
            className={
              'flex flex-col items-center gap-4 bg-light p-4 rounded-sm-5 text-sm font-semibold'
            }
          >
            <Spinner className={'!w-4 !border-[3px] !border-r-ebony_opacity_50'} />
            <p>Checking Zendesk Connection...</p>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-2 max-h-[500px] overflow-auto px-4">
        <ZendeskHelpCenterIsPrivate control={control} />
        <Controller
          control={control}
          name="enabled"
          render={({ field: { onChange, value } }) => (
            <div className={'flex items-center gap-2.5'}>
              <Switch
                type="accent"
                id="zendesk-help-center-auto-sync"
                checked={value}
                onCheckedChange={onChange}
                disabled={isTrialPlan}
              />
              <label
                htmlFor="zendesk-help-center-auto-sync"
                className={'text-sm font-medium text-ebony_opacity_80'}
              >
                Auto-Sync
              </label>
              <InputInfoToolTip
                infoToolTip={{
                  side: 'right',
                  description: isTrialPlan ? (
                    <>Unavailable for trial plan</>
                  ) : (
                    <>Sync new content and updates in real time</>
                  ),
                }}
              />
            </div>
          )}
        />
        <Controller
          control={control}
          name="domain"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              type={'text'}
              label={'Zendesk help center domain'}
              placeholder={'.domain.com'}
              invalidMessage={error?.message}
              value={value}
              isValid={!error?.message}
              onChange={(e) => {
                const res = getSubdomainHelper(e.target.value);
                if (res?.subdomain && res?.domain) {
                  onChange(`${res?.subdomain}.${res?.domain}`);
                } else if (res?.domain) {
                  onChange(res?.domain);
                } else {
                  onChange(e.target.value);
                }
              }}
              infoTooltip={{
                side: 'right',
                description: <>Link to your Zendesk Help Center</>,
              }}
            />
          )}
        />
        <ZendeskHelpCenterAddUrls control={control} clearErrors={clearErrors} />
        <ZendeskHelpCenterExcludedUrls control={control} clearErrors={clearErrors} />
        {needsCredentials && (
          <>
            <Controller
              control={control}
              name="zendesk_subdomain"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Input
                  type={'text'}
                  label={'Zendesk API Subdomain'}
                  placeholder={'brand'}
                  invalidMessage={error?.message}
                  value={value}
                  isValid={!error?.message}
                  onChange={(e) => {
                    const res = getSubdomainHelper(e.target.value);
                    if (res?.subdomain) {
                      onChange(res?.subdomain);
                    } else {
                      onChange(e.target.value);
                    }
                  }}
                  infoTooltip={{
                    side: 'right',
                    description: (
                      <>
                        <a
                          href={ZENDESK_HELP_CENTER_URL.API_SUBDOMAIN}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline cursor-pointer"
                        >
                          Here
                        </a>{' '}
                        is where to find it
                      </>
                    ),
                  }}
                />
              )}
            />
            <Controller
              control={control}
              name="brand_id"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Input
                  type={'text'}
                  label={'Zendesk Brand ID'}
                  placeholder={'-------------------'}
                  invalidMessage={error?.message}
                  value={value}
                  isValid={!error?.message}
                  onChange={onChange}
                  infoTooltip={{
                    side: 'right',
                    description: (
                      <>
                        Check how to find your Brand ID{' '}
                        <a
                          href={ZENDESK_HELP_CENTER_URL.BRAND_ID}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline cursor-pointer"
                        >
                          here →
                        </a>
                      </>
                    ),
                  }}
                />
              )}
            />
            <ZendeskConnectSection
              className={'mt-3'}
              isZendeskAuthorized={isZendeskAuthorized}
              isManualConnectActive={isManualConnectActive}
              onConnectWithZendesk={connectWithZendesk}
              onManualConnectOpenChange={setIsManualConnectOpen}
            >
              <Controller
                control={control}
                name="zendesk_email"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    type={'email'}
                    label={'Zendesk email'}
                    placeholder={'email@zendesk.com'}
                    invalidMessage={error?.message}
                    value={value}
                    isValid={!error?.message}
                    onChange={onChange}
                    infoTooltip={{
                      side: 'right',
                      description: (
                        <>Email address of the Zendesk user used to generate the API key</>
                      ),
                    }}
                  />
                )}
              />
              <Controller
                control={control}
                name="zendesk_api_key"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    type={'password'}
                    label={'Zendesk API key'}
                    placeholder={'------------------------------------------------------'}
                    invalidMessage={error?.message}
                    value={value}
                    isValid={!error?.message}
                    onChange={onChange}
                    infoTooltip={{
                      side: 'right',
                      description: (
                        <>
                          Check how to generate the API Key{' '}
                          <a
                            href={ZENDESK_HELP_CENTER_URL.API_KEY}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline cursor-pointer"
                          >
                            here →
                          </a>
                        </>
                      ),
                    }}
                  />
                )}
              />
            </ZendeskConnectSection>
          </>
        )}
      </div>
      <div className="flex justify-center mt-5 px-4">
        <div className="relative flex gap-2">
          <Button variant="tertiary" type="button" className="w-[100px]" onClick={() => close()}>
            Cancel
          </Button>
          <Button variant="dark" type="submit" className="w-[100px]" disabled={isIntegrateDisabled}>
            Integrate
          </Button>
          {backButtonVisible && (
            <Button
              variant="ghost-grey"
              type="button"
              className="absolute left-full ml-8 w-[100px]"
              onClick={prev}
            >
              <img
                src={ArrowLargeGreyIcon}
                alt={''}
                width={16}
                height={16}
                className={'rotate-180'}
              />
              Back
            </Button>
          )}
        </div>
      </div>
    </form>
  );
};
